import { OutlinedPrimaryButton } from '@monorepo/shared/componentsV2/Button/outlined/OutlinedPrimaryButton';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { format } from 'date-fns';
import { FORM_TEMPLATE_GENERATION_FILE_SIZE_LIMIT_IN_MB } from 'mapistry-shared';
import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import styled from 'styled-components';
import { InputLabel } from '../../../../shared/componentsV2/Input';
import { Loading } from '../../../../shared/componentsV2/Loading';
import { PdfPreview } from './PdfPreview';

// this could / should be a Mui input, styling this got de-prioritized for speed (July 2025)
const StyledFileInput = styled.input`
  width: 100%;
  margin-top: 0.5rem;
`;

const StyledProcessFileButton = styled(OutlinedPrimaryButton)`
  margin-top: 1rem;
`;

const StyledProcessingInfo = styled.div`
  margin-top: 1rem;
  font-style: italic;
`;

export type HandleUploadPdf = ({ file }: { file?: File }) => void;

export function PdfUploader({
  handleUpload,
  isProcessingImage,
  startedProcessingAt,
}: {
  handleUpload: HandleUploadPdf;
  isProcessingImage: boolean;
  startedProcessingAt?: Date;
}) {
  const [file, setFile] = useState<File>();
  const [fileBlobUrl, setFileBlobUrl] = useState<string>();

  const { error: showErrorToast } = useToast();

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFile = e.target.files?.[0];

      if (!newFile) {
        // file picker was cleared out
        setFile(undefined);
        return;
      }

      // validate file size
      const fileSizeInMB = newFile.size / 1024 / 1024;
      if (fileSizeInMB > FORM_TEMPLATE_GENERATION_FILE_SIZE_LIMIT_IN_MB) {
        setFile(undefined);
        showErrorToast(
          `File size is too large. Please upload a file smaller than ${FORM_TEMPLATE_GENERATION_FILE_SIZE_LIMIT_IN_MB}MB.`,
          { dontAutoHide: true },
        );
        return;
      }

      setFile(newFile);
    },
    [showErrorToast],
  );

  useEffect(() => {
    if (!file) {
      setFileBlobUrl(undefined);
      return;
    }

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onloadend = () => {
      const blob = new Blob([reader.result as ArrayBuffer], {
        type: file.type, // e.g. usually application/pdf
      });
      const blobUrl = URL.createObjectURL(blob);
      setFileBlobUrl(blobUrl);
    };
  }, [file]);

  return (
    <div>
      <Form onSubmit={() => handleUpload({ file })}>
        {({ handleSubmit }) => (
          <>
            <InputLabel>
              Select the pdf file you want to use as the starting point for
              creating your form template:
              <StyledFileInput
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                disabled={isProcessingImage}
              />
            </InputLabel>

            <StyledProcessFileButton
              disabled={!fileBlobUrl || isProcessingImage}
              type="submit"
              onClick={handleSubmit}
              variant="outlined"
              startIcon={
                isProcessingImage ? <Loading size="small" /> : undefined
              }
            >
              {isProcessingImage ? 'Processing...' : 'Process with AI'}
            </StyledProcessFileButton>
          </>
        )}
      </Form>

      {startedProcessingAt && (
        <StyledProcessingInfo>
          <div>Started processing at {format(startedProcessingAt, 'pp')}.</div>
          <div>
            This might take a while - you can work in other tabs, but do not
            close this tab while it is processing. If it has been more than 30
            minutes, refresh the page and try again.
          </div>
        </StyledProcessingInfo>
      )}

      {fileBlobUrl && <PdfPreview fileBlobUrl={fileBlobUrl} />}
    </div>
  );
}
